import { displayTitle, processedTokens } from "@szyos/domain";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, errorMessage } from "../api";
import { EmptyState } from "../components/feedback";
import { EfficiencyPanel } from "../components/Efficiency";
import { LimitList, TokenFigure } from "../components/Usage";
import { CostRing } from "../components/widgets";
import { STATE_LABEL, dateTime, tokens } from "../format";
import { Icon } from "../icons";
import type { WorkOrderState } from "@szyos/contracts";

/** Token-Verbrauch je Projekt, Auftrag und Aufgabe, aktuelle Woche und insgesamt, mit Abo-Limitstand. */
export function Usage() {
  const usage = useQuery({ queryKey: ["usage"], queryFn: api.usage });
  const costs = useQuery({ queryKey: ["costs"], queryFn: api.costsToday });
  if (usage.isError) {
    return <EmptyState icon="alert" title="Verbrauch nicht geladen" text={errorMessage(usage.error)} />;
  }
  if (!usage.data) return <p className="muted">Lade Verbrauch …</p>;
  const u = usage.data;
  const weekText =
    u.window.source === "abo"
      ? `Woche bis ${dateTime(u.window.to)} Uhr, wie dein Abo sie zählt.`
      : `Kalenderwoche ab Montag ${dateTime(u.window.from)} Uhr. Sobald Claude die Rücksetzzeit deines Wochenlimits meldet, gilt diese.`;

  return (
    <>
      <section className="hero" aria-labelledby="heroTitle">
        <div className="hero-text">
          <h1 id="heroTitle">
            Token-<span className="glow-word">Verbrauch.</span>
          </h1>
          <p className="hero-sub">{weekText}</p>
        </div>
      </section>

      <EfficiencyPanel detailed />

      <section className="usage-grid">
        <div className="panel usage-panel" aria-labelledby="usageSumTitle">
          <div className="panel-head">
            <h2 id="usageSumTitle">Deine Projekte in szyOS</h2>
          </div>
          <div className="token-figures">
            <TokenFigure tally={u.week} label="diese Woche" />
            <TokenFigure tally={u.total} label="insgesamt" />
          </div>
          <p className="muted small">
            Gezählt werden neu verarbeitete Tokens: Eingabe ohne Cache-Lesezugriffe plus Ausgabe.
            Cache-Lesezugriffe stehen getrennt daneben, sie belasten das Abo deutlich weniger.
          </p>
        </div>

        <div className="panel usage-panel" aria-labelledby="usageLimitTitle">
          <div className="panel-head">
            <h2 id="usageLimitTitle">Abo-Limit</h2>
          </div>
          <LimitList limits={u.limits} />
          <p className="muted small">
            Anthropic nennt für das Abo keine feste Tokenmenge. szyOS zeigt nur, was Claude Code beim letzten
            Lauf gemeldet hat, und schätzt nichts dazu.
          </p>
        </div>
      </section>

      <section className="panel usage-panel" aria-labelledby="usageProjectsTitle">
        <div className="panel-head">
          <h2 id="usageProjectsTitle">Je Projekt und Auftrag</h2>
        </div>
        {u.projects.length === 0 ? (
          <EmptyState icon="folder" title="Noch keine Projekte" text="Verbrauch erscheint hier je Projekt." />
        ) : (
          <ul className="usage-projects">
            {u.projects.map((p) => (
              <li key={p.projectId} className="usage-project">
                <div className="usage-project-head">
                  <Link to="/projects/$projectId" params={{ projectId: p.projectId }} className="link">
                    <strong>{p.title}</strong>
                  </Link>
                  <span className="mono">
                    {tokens(processedTokens(p.week))} diese Woche · {tokens(processedTokens(p.total))} gesamt
                  </span>
                </div>
                {p.planning.runs > 0 && (
                  <p className="muted small">
                    Ideenklärung im Planungsstudio: {tokens(processedTokens(p.planning))}
                  </p>
                )}
                {p.workOrders.length > 0 && (
                  <ul className="usage-orders">
                    {p.workOrders.map((w) => (
                      <li key={w.workOrderId}>
                        <details>
                          <summary>
                            <span className="usage-order-title">{w.title}</span>
                            <span className={`chip state-chip state-${w.state.toLowerCase()}`}>
                              {STATE_LABEL[w.state as WorkOrderState] ?? w.state}
                            </span>
                            <span className="mono">{tokens(processedTokens(w.total))}</span>
                          </summary>
                          <ul className="usage-items">
                            {w.planning.runs > 0 && (
                              <li>
                                <span>Planvorschlag (Planungsstudio)</span>
                                <span className="mono">{tokens(processedTokens(w.planning))}</span>
                              </li>
                            )}
                            {w.items.map((i) => (
                              <li key={i.workItemId}>
                                <span>{displayTitle(i.title)}</span>
                                <span className="mono">
                                  {tokens(processedTokens(i.total))} · {i.total.runs}{" "}
                                  {i.total.runs === 1 ? "Lauf" : "Läufe"}
                                </span>
                              </li>
                            ))}
                            {w.items.length === 0 && w.planning.runs === 0 && (
                              <li className="muted">Noch keine Aufgaben.</li>
                            )}
                          </ul>
                          <Link
                            to="/work-orders/$orderId"
                            params={{ orderId: w.workOrderId }}
                            className="link"
                          >
                            Zum Auftrag <Icon name="arrow" />
                          </Link>
                        </details>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        {u.ideas.total.runs > 0 && (
          <p className="muted small">
            Ideen, die noch kein Projekt sind (Planungsstudio): {tokens(processedTokens(u.ideas.week))} diese
            Woche, {tokens(processedTokens(u.ideas.total))} gesamt.
          </p>
        )}
        {u.simulated.total.runs > 0 && (
          <p className="muted small">
            {u.simulated.total.runs === 1
              ? "Ein simulierter Lauf (Demo, Mock) zählt nicht mit."
              : `${u.simulated.total.runs} simulierte Läufe (Demo, Mock) zählen nicht mit.`}
          </p>
        )}
      </section>
      {/* API-Kosten von heute; aus dem Cockpit hierher verlegt (UX-Umbau, Phase 2). */}
      <section className="cost-today" aria-label="API-Kosten heute">
        <CostRing rows={costs.data ?? []} budgetUsd={null} title="API-Kosten heute" />
      </section>
    </>
  );
}
